import { CollaborationController } from 'libs/collaboration';
import { Metadata, SocketConfig } from 'libs/collaboration/collaboration.types';
import { SOCKET_EVENTS } from 'libs/collaboration/socket.config';
import { fetchCollaborationConfig } from 'libs/e1api/design';
import { setShowCollaborationWarning, setTeamSharingEnabled } from 'modules/Editor/store/editorSettingsSlice';
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '../Editor/store';
import { selectIsTeamSharingEnabled } from '../Editor/store/editorSettingsSelector';

type CollaborationLoaderProps = {
  children: ReactNode;
  infographId: string;
  userId: string;
};

type CollaborationConfig = { multipage_metadata: Metadata; sockets: SocketConfig; team_share_enabled: boolean };

/**
 * Initialize the collaboration instance and load.
 *
 * First, It checks the current environment, load the socket config, and see if it needs to enable collaboration.
 * If it's ready to use the collaboration, it will initialize the collaboration instance and using the socket config.
 *
 * @param children
 * @returns {ReactElement}
 */
export const CollaborationLoader: React.FC<CollaborationLoaderProps> = ({
  children,
  infographId,
  userId,
}: CollaborationLoaderProps) => {
  const dispatch = useAppDispatch();
  const collaboration = useRef<CollaborationController | null>(null);
  const isLoading = useRef(false);
  const isLoaded = useRef(false);
  const configPayload = useRef<CollaborationConfig>();
  const isTeamSharingEnabled = useAppSelector(selectIsTeamSharingEnabled);

  // Fetch the collaboration config using e1api
  const initConfigData = useCallback(async () => {
    if (!infographId) return;

    isLoading.current = true;

    try {
      const res = await fetchCollaborationConfig(infographId);

      if (res.message === 'OK') {
        const {
          payload: { multipage_metadata: metadata, sockets: socketConfig, team_share_enabled },
        } = res;

        configPayload.current = {
          multipage_metadata: metadata,
          sockets: socketConfig,
          team_share_enabled,
        };

        if (team_share_enabled) {
          dispatch(setTeamSharingEnabled(team_share_enabled));
        }
        isLoading.current = false;
        isLoaded.current = true;
      }
    } catch (error) {
      isLoading.current = false;
      isLoaded.current = false;
      console.error('Error collaboartion config fetch');
    }
  }, [infographId, dispatch]);

  // Initialize the collaboration controller
  const initCollaborationController = useCallback(async () => {
    if (!configPayload.current) return;

    try {
      const { multipage_metadata: metadata, sockets: socketConfig, team_share_enabled } = configPayload.current;
      // Check both team_share_enabled and isTeamSharingEnabled to make sure the team sharing is enabled.
      // - team_share_enabled is from the e1 API
      // - isTeamSharingEnabled is from the e2 state
      if (team_share_enabled || isTeamSharingEnabled) {
        collaboration.current = new CollaborationController(userId, metadata, socketConfig);
        collaboration.current.rtc();

        collaboration.current.on(SOCKET_EVENTS.INFOGRAPHICS_IN_EDITED, () => {
          if (collaboration?.current?.hasSocketUsers) dispatch(setShowCollaborationWarning(true));
        });

        collaboration.current.onError = (errorType) => {
          console.error('socket error:', errorType);
        };
      }
    } catch (error) {
      console.error('Error collaboartion loader');
    }
  }, [userId, isTeamSharingEnabled, dispatch]);

  // Initialize for the first load
  useEffect(() => {
    if (isLoading.current === true && isLoaded.current === false) return;

    initConfigData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manage RTC regrading team share toggle state
  useEffect(() => {
    if (isLoaded.current === false) return;

    const enableRTC = isTeamSharingEnabled;
    const disableRTC = !isTeamSharingEnabled && collaboration?.current?.isTeamShared === true;

    if (enableRTC) {
      initConfigData(); // Refetch the config data to get the latest socket config
      initCollaborationController();
    } else if (disableRTC) {
      collaboration?.current?.disableShare();
      collaboration.current = null;
    }
  }, [isTeamSharingEnabled, initConfigData, initCollaborationController]);

  return <>{children}</>;
};

export default CollaborationLoader;
